import { JSX, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Flex } from '@chakra-ui/react'
import { OrderPaymentStatus, PickupStatus } from '@leid/api-orders'
import { isPast } from 'date-fns'
import Router from 'next/router'

import ProductInfoButtons from 'app/components/shared/ProductInfoButtons'
import { OrderItemInfo, ProlongationInfo } from 'appTypes/orders'
import { LeidButton, LeidButtonWhiteOutlined, LeidYellowButton } from 'components/ui/Buttons/Button'
import { Loader } from 'components/ui/Loader'
import apiClient from 'helpers/clients/apiClient'
import { useCustomerInfo } from 'hooks/useCustomerInfo'
import { resetCart, setProlongationData } from 'store/partials/cart/cartSlice'
import { displaySnackbar } from 'store/partials/snackbar/snackbarSlice'
import { getPaymentSelectPageUrl, getProlongationPageUrl } from 'utils/redirectUrlsBuilders'
import { removeDecimals } from 'utils/removeDecimals'
import { sortProlongations } from 'utils/sortProlongations'
import OrderItemCancellButton from './OrderItemCancellButton'

type Props = {
  orderItemInfo: OrderItemInfo
  refetchOrder: () => void
}

const OrderItemActionButtons = ({ orderItemInfo, refetchOrder }: Props): JSX.Element | null => {
  const { t } = useTranslation('common')

  const dispatch = useDispatch()
  const { customerInfo } = useCustomerInfo()

  const [isNewestProlongationConfirmed, setIsNewestProlongationConfirmed] = useState(true)
  const [isProlongButtonDisabled, setIsProlongButtonDisabled] = useState(true)
  const [sortedProlongations, setSortedProlongations] = useState<ProlongationInfo[]>([])
  const [disabledCancelOrderButton, setDisabledCancelOrderButton] = useState<boolean>(false)

  useEffect(() => {
    const sortedProlongations = sortProlongations(orderItemInfo)
    setSortedProlongations(sortedProlongations)

    const areAnyProlongationsInOrder = sortedProlongations.length > 0
    const isNewestProlongationConfirmed = areAnyProlongationsInOrder ? sortedProlongations[0].confirmed : true
    setIsNewestProlongationConfirmed(isNewestProlongationConfirmed)

    const isReturnDateInPast = isRentedToInPast(orderItemInfo.orderItem.rentedTo)

    const isProlongButtonDisabled = isReturnDateInPast || !isNewestProlongationConfirmed
    setIsProlongButtonDisabled(isProlongButtonDisabled)
  }, [orderItemInfo])

  const isRentedToInPast = (rentedTo: Date | undefined): boolean => {
    if (!rentedTo) {
      return false
    }

    const rentedToDate = new Date(rentedTo)
    rentedToDate.setDate(rentedToDate.getDate() + 14)
    const isReturnDateInPast = isPast(rentedToDate)

    return isReturnDateInPast
  }

  const redirectToProlongationPaymentPage = () => {
    dispatch(setProlongationData(sortedProlongations[0]))
    const orderId = sortedProlongations[0].prolongation.orderId
    const orderItemId = sortedProlongations[0].prolongation.id
    const purchaseAs = customerInfo?.customer?.company?.registrationId ? 'organization' : 'person'
    Router.push(getPaymentSelectPageUrl(orderId, orderItemId, purchaseAs))
  }

  const checkIfAuthenticated = (prolongationPageUrl: string): boolean => {
    if (customerInfo.customer) {
      return true
    } else {
      const redirectUrl = `/login?redirectTo=${encodeURIComponent(`${prolongationPageUrl}`)}`

      Router.push(redirectUrl)
      return false
    }
  }

  const redirectToProlongationPage = () => {
    const prolongationPageUrl = getProlongationPageUrl(orderItemInfo)

    const isAuthenticated = checkIfAuthenticated(prolongationPageUrl)
    if (!isAuthenticated) {
      return
    }
    Router.push(prolongationPageUrl)
  }

  const cancelOrder = async () => {
    setDisabledCancelOrderButton(true)

    try {
      const cancelResponse = await apiClient.cancelOrder(sortedProlongations[0].prolongation.orderId)
      if (!cancelResponse.success || !cancelResponse.data) {
        dispatch(displaySnackbar({ message: t('error'), isError: true }))
        return
      }

      dispatch(displaySnackbar({ message: t('paymentWasCanceled'), isError: false }))
      dispatch(resetCart())

      refetchOrder()
    } finally {
      setDisabledCancelOrderButton(false)
    }
  }

  const isReturned = [PickupStatus.RETURNED, PickupStatus.INSPECTED, PickupStatus.COMPLETED].includes(
    orderItemInfo.orderItem.pickupStatus,
  )

  const isCanceled =
    orderItemInfo.orderItem.status === OrderPaymentStatus.canceled ||
    orderItemInfo.orderItem.status === OrderPaymentStatus.paidAndCanceled

  if (orderItemInfo.orderItem.associatedParentOrderItemId !== undefined) {
    return null
  }

  return (
    <Flex direction="column" alignItems="center" alignSelf="stretch" gridGap="12px">
      {/* TODO: Implement in version 2 */}
      {/* <LeidButton>Bruksanvisning</LeidButton> */}

      {!isNewestProlongationConfirmed ? (
        <>
          <LeidYellowButton onClick={redirectToProlongationPaymentPage}>
            {t('payForProlongation')} ({removeDecimals(sortedProlongations[0].prolongation.price)} {t('NOK')})
          </LeidYellowButton>

          <LeidButtonWhiteOutlined onClick={cancelOrder} isDisabled={disabledCancelOrderButton}>
            {disabledCancelOrderButton ? <Loader></Loader> : t('cancelPayment')}
          </LeidButtonWhiteOutlined>
        </>
      ) : (
        <>
          {orderItemInfo.orderItem.associatedParentOrderItemId === undefined && (
            <LeidButton onClick={redirectToProlongationPage} isDisabled={isProlongButtonDisabled}>
              {t('extendRentalPeriod')}
            </LeidButton>
          )}
        </>
      )}

      {!isReturned && !isCanceled && (
        <>
          <ProductInfoButtons
            masterArticle={orderItemInfo.masterArticle}
            relatedArticle={orderItemInfo.relatedArticle}
            location={orderItemInfo.location}
            leidButtons
          />
        </>
      )}

      <OrderItemCancellButton orderItemInfo={orderItemInfo} refetchOrder={refetchOrder} />
    </Flex>
  )
}

export default OrderItemActionButtons
